import { assemble, chain, isGreaterThanOrEqual, isNumeric, isRequired } from '../../../utils/i18nValidators';
import { validator } from 'simple-object-validation';

import container from '../../../container';

function hasFileId(file) {
  return !!file.response?.body?.id;
}

const fileIsRequired = validator(
  (files) => !!files?.length && files.every(hasFileId),
  (param, name) => {
    const translatedName = container.i18n.t(name);
    return container.i18n.t('{{name}} is required', { name: translatedName });
  }
);

const fileIsRequiredIf = (condition, fileName) => {
  if (condition) {
    return fileIsRequired(fileName);
  }

  return () => undefined;
};

const weightIsRequired = validator(
  (value) => value !== null && isNumeric(value) && value > 0,
  (param, name) => {
    const translatedName = container.i18n.t(name);
    return container.i18n.t('{{name}} is required', { name: translatedName });
  }
);

const maxChars = validator((string, length) => !string || string.length < length, {
  expectParameter: true,
  messageCreator: (length, name) => {
    const translatedName = container.i18n.t(name);
    return container.i18n.t('{{name}} must be smaller than {{length}} characters', {
      name: translatedName,
      length,
    });
  },
});

const trackingInfoValidation = {
  trackingCode: isRequired('Tracking ID'),
};

export const trackingInfoValidator = assemble(trackingInfoValidation);

const shippingInfoValidation = {
  trackingCode: isRequired('Tracking ID'),
  recipientName: isRequired('Customer name'),
  recipientAddress: isRequired('Recipient address'),
  recipientCity: isRequired('Recipient city'),
  recipientZipCode: isRequired('Recipient zip code'),
  recipientCountryCode: isRequired('Recipient country'),
};

export const getShippingInfoValidator = (isCashOnDeliveryClaim, isTrackingScreenshotRequired, isWeightRequired) => {
  const validation = {
    ...shippingInfoValidation,
    ...(!isCashOnDeliveryClaim && isWeightRequired ? { weight: weightIsRequired('Weight') } : {}),
    ...(isTrackingScreenshotRequired ? { trackingScreenshot: fileIsRequired('Tracking screenshot') } : {}),
  };

  return assemble(validation);
};

export const invoiceInfoValidator = assemble({
  netValue: chain([isRequired, isNumeric, isGreaterThanOrEqual(0)])('Net value'),
  invoiceDocuments: fileIsRequired('Invoice documents'),
  shopClaimId: maxChars(64)('Internal claim ID'),
});

export const wholeDamageInfoValidator = assemble({
  wholeDamageInfo: isRequired('Whole damage info'),
  picturesOfDamage: fileIsRequired('Pictures of damage'),
  lmcCaseId: maxChars(64)('Carrier case ID'),
});

export function isEmptyValidationResult(result) {
  return result && Object.keys(result).length === 0;
}

function damageInfoValidator(value) {
  if (!Array.isArray(value)) {
    return 'Damage info must be an array';
  }

  if (value.length === 0) {
    return 'Damage info can not be empty';
  }

  const shapeValidator = assemble({
    amount: chain([isRequired, isNumeric, isGreaterThanOrEqual(1)])('Amount'),
    itemName: isRequired('Item name'),
    damageDescription: isRequired('Demage description'),
  });

  const results = {};

  value.forEach((info) => {
    const result = shapeValidator(info);

    if (!isEmptyValidationResult(result)) {
      results[info.id] = result;
    }
  });

  return isEmptyValidationResult(results) ? undefined : results;
}

export const partialDamageInfoValidator = assemble({
  partialDamageInfo: damageInfoValidator,
  picturesOfDamage: fileIsRequired('Pictures of damage'),
  lmcCaseId: maxChars(64)('Carrier case ID'),
});

function missingItemValidator(value) {
  if (!Array.isArray(value)) {
    return 'Missing item info must be an array';
  }

  if (value.length === 0) {
    return 'Missing item info can not be empty';
  }

  const shapeValidator = assemble({
    amount: chain([isRequired, isNumeric, isGreaterThanOrEqual(1)])('Amount'),
    itemName: isRequired('Item name'),
  });

  const results = {};

  value.forEach((info) => {
    const result = shapeValidator(info);

    if (!isEmptyValidationResult(result)) {
      results[info.id] = result;
    }
  });

  return isEmptyValidationResult(results) ? undefined : results;
}

export const createMissingItemInfoValidator = ({ isPicturesOfMissingItemRequired }) =>
  assemble({
    missingItemInfo: missingItemValidator,
    picturesOfMissingItem: fileIsRequiredIf(isPicturesOfMissingItemRequired, 'Pictures of missing item'),
    lmcCaseId: maxChars(64)('Carrier case ID'),
  });
